<template>
  <Modal class="ModalImageSelect" @close="$emit('close')">
    <div class="modalContent">
      <TabsHead
        :indexActive.sync="tabIndexActive"
        :labels="tabsLabels" />
      <div class="searchWrap">
        <Field
          icon="search"
          :placeholder="$t('search')"
          :value.sync="imageSearchValue"
          @keypress.enter.prevent.native="getImagesSuggested" />
      </div>
      <!--
      <div
        v-if="imagePhrasesSuggested"
        class="phrasesContainer">
        <Row>
          <Column :md="8">
            <Tag
              v-for="phrase in imagePhrasesSuggested"
              :key="phrase"
              @click="getImagesByPhrase(phrase)">
              {{ phrase }}
            </Tag>
          </Column>
          <Column v-if="text" :md="4">
            <Btn
              class="sm"
              :isLoading="isGenerating"
              @click="generateImagePhrases">
              {{ $t('suggestImages') }}
            </Btn>
          </Column>
        </Row>
      </div>
      -->
      <Masonry class="imagesContainer">
        <div
          v-for="image in imagesMasonry"
          :key="image.url"
          class="imageWrap"
          :class="{
            active: fileId && fileId === image.fileId,
            used: isImageUsed(image),
          }"
          @click="setFile(image)"
          @keydown.enter="setFile(image)">
          <div class="source">
            {{ image.source }}
          </div>
          <Pic :src="image.thumbnail" />
        </div>
      </Masonry>
      <div
        v-if="isSearching"
        class="loaderWrap">
        <Loader class="lg" />
      </div>
    </div>
  </Modal>
</template>

<script>
import Masonry, { sortMasonry } from '@components/Masonry'
import { actions, getters } from '@utils'
import { pluck, propEq } from 'rambda'
import { IMAGE_SEARCH_SOURCES } from '@store/modules/imageSearch'
import TabsHead from '@components/TabsHead'
import { mergeLabelArrays } from '@utils/collections'

export default {
  components: {
    Masonry,
    TabsHead,
  },
  props: {
    article: Object,
    fileId: Number,
    section: Object,
    type: String,
  },
  data() {
    const tabLibrary = 'library'
    const tabSearch = 'search'

    const imageSearchValue = (() => {
      const sectionTemplate = this.$store.getters
        .queryOne('sectionTemplate', this.section?.sectionTemplateId)

      const sectionTemplateName = sectionTemplate?.hasTitle
        ? sectionTemplate?.nameAlt
        : null

      return [
        this.article?.topic,
        sectionTemplateName,
      ].filter(Boolean).join(' ')
    })()

    return {
      tabLibrary,
      tabSearch,

      tabIndexActive: 0,
      tabs: mergeLabelArrays([
        { tab: tabLibrary },
        { tab: tabSearch },
      ], this.$t('tabs')),

      imageSearchValue,
      images: [],
      isGenerating: false,
      isSearching: false,
      lastSearchPhrase: null,
    }
  },
  computed: {
    fileImage() {
      if (!this.fileId) return

      const file = this.queryOne('file', this.fileId)
      const url = this.getFileUrl(file)

      return {
        alt: 'N/A',
        fileId: file.id,
        similarity: null,
        source: 'Selected',
        thumbnail: url,
        url,
      }
    },
    fileIdsUsed() {
      const sections = (() => {
        if (this.article.sections) {
          return this.article.sections
        }

        if (this.article.id) {
          return this.query('section', { articleId: this.article.id })
        }

        return []
      })()

      return [
        this.article.imageFileId,
        ...pluck('imageFileId', sections),
      ].filter(Boolean)
    },
    imagesMasonry() {
      return sortMasonry(4, this.images)
    },
    tabActive() {
      return this.tabs[this.tabIndexActive]
    },
    tabsLabels() {
      return pluck('label', this.tabs)
    },
    ...getters('getFileUrl'),
  },
  methods: {
    async setFile({ fileId, url }) {
      this.$wrap(async () => {
        if (fileId) {
          await this.FETCH_FILE(fileId)
        }

        fileId = fileId || await (async () => {
          const { file } = await this.CREATE_UPLOAD({
            scope: 'PUBLIC',
            type: this.type,
            url,
            fileName: this.imageSearchValue,
          })

          return file.id
        })()

        const file = this.queryOne('file', fileId)

        const imageAlt = file?.label || this.imageSearchValue

        this.$up('imageAlt', imageAlt)
        this.$up('fileId', fileId)
        this.$up('fields', {
          imageFileId: fileId,
          imageAlt,
        })
        this.$emit('close')
      }, 'isUploading')
    },
    // async getImagesByPhrase(phrase) {
    //   this.imageSearchValue = phrase
    //   await this.getImagesSuggested()
    // },
    async getImagesSuggested() {
      if (this.lastSearchPhrase === this.imageSearchValue) return

      const sources = {
        [this.tabLibrary]: [
          IMAGE_SEARCH_SOURCES.LIBRARY,
        ],
        [this.tabSearch]: [
          IMAGE_SEARCH_SOURCES.EXTERNAL,
        ],
      }[this.tabActive.tab]

      this.images = []
      const images = await this.$wrap(
        () => this.FIND_IMAGE_SEARCH({
          search: this.imageSearchValue,
          sources,
          $limit: 20,
        }),
        'isSearching',
      )

      const shouldPrependSelectedImage = this.file && images
        .some(propEq('fileId', this.file.id))

      this.images = [
        ...shouldPrependSelectedImage
          ? [this.file]
          : [],
        ...images,
      ]

      this.lastSearchPhrase = this.imageSearchValue
    },
    isImageUsed({ fileId }) {
      if (!fileId) return false

      return this.fileIdsUsed.includes(fileId)
    },
    // async generateImagePhrases() {
    //   if (this.isGenerating) return

    //   const { images } = (await this
    //     .$wrap(this.CREATE_COMPLETION({
    //       type: 'TRANSFORM',
    //       promptTemplateName: 'DECORATIONS_IMAGES',
    //       category: this.category,
    //       fields: {
    //         topic: this.topic,
    //         text: this.text,
    //       },
    //     }), 'isGenerating'))
    //     .variants[0]

    //   this.$emit('update:imagePhrasesSuggested', pluck('description', images))
    // },
    ...actions(
      // 'CREATE_COMPLETION',
      'CREATE_UPLOAD',
      'FETCH_FILE',
      'FIND_IMAGE_SEARCH',
    ),
  },
  watch: {
    tabIndexActive: {
      immediate: true,
      handler() {
        this.lastSearchPhrase = null

        return this.getImagesSuggested()
      },
    },
  },
}
</script>

<style lang="scss">
.ModalImageSelect {
  .TabsHead {
    margin: 0;
  }

  .ModalClose {
    background: rgba($neutral-light, 0.8);
    border-radius: 0.5rem;
    padding: 0.25rem;
    right: 1.5rem;
  }

  .modalContent {
    height: 90vh;
    overflow-y: auto;

    .searchWrap {
      padding: 1rem 1rem 0;
    }

    .phrasesContainer {
      padding: 0 1rem 1rem;

      .Tag {
        cursor: pointer;
        margin: 0.5rem 0.5rem 0 0;
      }
    }

    .imagesContainer {
      padding: $u;
      width: 100%;

      .imageWrap {
        border-radius: $radius;
        height: auto;
        margin-bottom: $u;
        overflow: hidden;
        position: relative;

        .source {
          background: rgba($white, 0.6);
          border-radius: $radius;
          bottom: 0.5rem;
          left: 0.5rem;
          padding: 0.2rem;
          position: absolute;
          z-index: 1;
        }

        .Pic {
          cursor: pointer;
          display: block;
          width: 100%;

          img {
            @include trans;
            display: block;
            width: 100%;

            &:hover {
              opacity: 0.6;
            }
          }
        }

        &.active {
          border: 4px solid $info;

          img {
            opacity: 0.7;
          }
        }

        &.used {
          filter: grayscale(100%);
          opacity: 0.2;
        }
      }
    }
  }

  .loaderWrap {
    align-items: center;
    background: rgba($white, 0.6);
    display: flex;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 1;

    .Loader {
      height: 1.5rem;
      width: 1.5rem;
    }
  }
}
</style>

<i18n>
{
  "en": {
    "search": "Search",
    "tabs": [
      "Library",
      "Search"
    ]
  }
}
</i18n>
